import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDndMonitor } from '@dnd-kit/core'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PostItem } from './PostItem'
import { TimeSlot } from './TimeSlot'
import { CalendarControls } from './CalendarControls'
import { getUserTimeZone, getClosestSupportedTimeZone } from './timezone'
import { HourGrouping } from './HourGrouping'
import { Button } from '@/components/ui/shadcn/button'
import type { DragPreview, DraggedCalendarPost } from '../types'
import {
  DAYS,
  HOURS,
  MINUTES,
  getWeekDates,
  getCurrentTimePosition,
  calculateScrollPosition,
  formatDragTime,
  snapToFifteenMinutes,
  isFirstInTimeGroup,
} from '../utils'
import { type Campaign } from '@/pages/schedule-agent'

export function Calendar({ initialPosts }: { initialPosts: Campaign['posts'] }) {
  const [currentTime, _setCurrentTime] = useState(new Date())
  const [selectedTimeZone, setSelectedTimeZone] = useState(
    getClosestSupportedTimeZone(getUserTimeZone()),
  )
  const [weekOffset, setWeekOffset] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null)
  const [_currentlyDraggedPost, setCurrentlyDraggedPost] = useState<
    Campaign['posts'][number] | null
  >(null)

  const [posts, setPosts] = useState<Campaign['posts']>(initialPosts)

  useEffect(() => {
    setPosts(initialPosts)
  }, [initialPosts])

  const weekDates = getWeekDates(weekOffset)

  const handleScrollToCurrentTime = useCallback(() => {
    const scrollPosition = calculateScrollPosition(currentTime, selectedTimeZone)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      })
    }
  }, [currentTime, selectedTimeZone])

  // Auto-scroll to current time on initial load
  useEffect(() => {
    const scrollToCurrentTime = () => {
      handleScrollToCurrentTime()
    }
    const timeout = setTimeout(scrollToCurrentTime, 100)
    return () => clearTimeout(timeout)
  }, [handleScrollToCurrentTime])

  // Navigation functions
  const goToPreviousWeek = () => setWeekOffset((prev) => prev - 1)
  const goToNextWeek = () => setWeekOffset((prev) => prev + 1)
  const goToCurrentWeek = () => setWeekOffset(0)

  // Post handling
  const handlePostMove = (
    postId: number,
    newDate: string,
    newHour: number,
    newMinute: number = 0,
  ) => {
    const validMinute = snapToFifteenMinutes(newMinute)
    const hh = String(newHour).padStart(2, '0')
    const mm = String(validMinute).padStart(2, '0')
    const updatedIso = new Date(`${newDate}T${hh}:${mm}:00`).toISOString()
    setPosts((prevPosts) =>
      prevPosts.map((post) => (post.id === postId ? { ...post, scheduledAt: updatedIso } : post)),
    )
  }

  const getPostForSlot = (date: Date, hour: number, minute: number = 0) => {
    const dateString = date.toISOString().split('T')[0]
    return posts.find((post) => {
      const d = new Date(post.scheduledAt)
      const eDate = d.toISOString().split('T')[0]
      const eHour = d.getHours()
      const eMinute = d.getMinutes()
      return eDate === dateString && eHour === hour && eMinute === minute
    })
  }

  // Drag handling
  const handleDragStart = (postId: number) => {
    const post = posts.find((e) => e.id === postId)
    if (post) {
      setCurrentlyDraggedPost(post)
    }
  }

  const handleDragEnd = () => {
    setCurrentlyDraggedPost(null)
    setDragPreview(null)
  }

  const handleDragPreview = (
    isVisible: boolean,
    hour?: number,
    minute?: number,
    date?: string,
    x?: number,
    y?: number,
    dragItem?: DraggedCalendarPost,
  ) => {
    if (
      isVisible &&
      hour !== undefined &&
      minute !== undefined &&
      date &&
      x !== undefined &&
      y !== undefined
    ) {
      if (dragItem) {
        const isDifferentPosition =
          dragItem.currentDate !== date ||
          dragItem.currentHour !== hour ||
          dragItem.currentMinute !== minute

        if (isDifferentPosition) {
          setDragPreview({ isVisible, hour, minute, date, x, y })
        } else {
          setDragPreview(null)
        }
      } else {
        setDragPreview({ isVisible, hour, minute, date, x, y })
      }
    } else {
      setDragPreview(null)
    }
  }

  useDndMonitor({
    onDragStart(event) {
      const data = event.active.data.current as DraggedCalendarPost | undefined
      if (data) {
        handleDragStart(data.id)
      }
    },
    onDragOver(event) {
      const { active, over } = event
      if (!over) {
        handleDragPreview(false)
        return
      }
      const overData = over.data.current as
        | { date: string; hour: number; minute: number }
        | undefined
      const dragItem = active.data.current as DraggedCalendarPost | undefined
      if (overData && dragItem) {
        // Use the activatorEvent (pointer position) for the preview tooltip coordinates
        const pointerEvent = event.activatorEvent as PointerEvent | undefined
        const x = pointerEvent?.clientX ?? 0
        const y = pointerEvent?.clientY ?? 0
        handleDragPreview(true, overData.hour, overData.minute, overData.date, x, y, dragItem)
      }
    },
    onDragEnd(event) {
      const { active, over } = event
      handleDragEnd()
      if (!over) return
      const postData = active.data.current as DraggedCalendarPost | undefined
      const slotData = over.data.current as
        | { date: string; hour: number; minute: number }
        | undefined
      if (!postData || !slotData) return
      if (
        postData.currentDate === slotData.date &&
        postData.currentHour === slotData.hour &&
        postData.currentMinute === slotData.minute
      ) {
        return
      }
      handlePostMove(postData.id, slotData.date, slotData.hour, slotData.minute)
    },
    onDragCancel() {
      handleDragEnd()
    },
  })

  const currentTimePosition = getCurrentTimePosition(currentTime, selectedTimeZone, weekDates)

  return (
    <div className="flex h-full flex-col bg-transparent">
      {/* Header */}
      <div className="shadow-xs backdrop-blur-xs shrink-0 rounded-t-3xl border-b border-slate-200/60 bg-white/90">
        {/* Controls Row */}
        <div className="bg-linear-to-r flex items-center justify-between border-b border-slate-100/60 from-slate-50/50 to-white px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold text-slate-900">Weekly Calendar</h1>
              <div className="h-5 w-px bg-slate-300"></div>
            </div>

            {/* Week Navigation */}
            <div className="flex items-center gap-1">
              <Button
                onClick={goToPreviousWeek}
                variant="outline"
                size="sm"
                className="h-8 w-8 border-slate-300 p-0 transition-all hover:border-slate-400 hover:bg-slate-50">
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {weekOffset !== 0 && (
                <Button
                  onClick={goToCurrentWeek}
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs text-[#6864ff] transition-all hover:bg-[#6864ff]/10 hover:text-[#4a36f5]">
                  Today
                </Button>
              )}

              <Button
                onClick={goToNextWeek}
                variant="outline"
                size="sm"
                className="h-8 w-8 border-slate-300 p-0 transition-all hover:border-slate-400 hover:bg-slate-50">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <span className="text-sm text-slate-600">
              {weekDates[0]!.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
              {weekDates[6]!.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>

          <CalendarControls
            selectedTimeZone={selectedTimeZone}
            onTimeZoneChange={setSelectedTimeZone}
            onScrollToCurrentTime={handleScrollToCurrentTime}
          />
        </div>

        <div className="grid grid-cols-8 gap-0">
          {/* Time column header */}
          <div className="border-r border-slate-200/60 p-6">
            <div className="text-sm font-medium text-slate-600">Time</div>
          </div>

          {/* Day headers */}
          {DAYS.map((day, index) => {
            const currentDate = weekDates[index]!
            const today = new Date()
            const isToday = currentDate.toDateString() === today.toDateString()
            const isCurrentWeek = weekOffset === 0

            return (
              <div
                key={day}
                className="relative border-r border-slate-200/60 p-6 text-center last:border-r-0">
                <div
                  className={`text-sm font-medium ${isToday && isCurrentWeek ? 'text-[#6864ff]' : 'text-slate-900'}`}>
                  {day}
                </div>
                <div
                  className={`mt-1 text-xs ${isToday && isCurrentWeek ? 'text-[#6864ff]' : 'text-slate-500'}`}>
                  {currentDate.getDate()}/{currentDate.getMonth() + 1}
                </div>
                {isToday && isCurrentWeek && (
                  <div className="absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 transform rounded-full border-2 border-white bg-[#6864ff] shadow-lg"></div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Calendar grid with scroll */}
      <div
        ref={scrollContainerRef}
        className="calendar-scroll-container relative flex-1 overflow-auto rounded-b-3xl">
        {/* Current time indicator */}
        {currentTimePosition && currentTimePosition.isToday && (
          <div
            className="pointer-events-none absolute z-10"
            style={{
              top: `${currentTimePosition.pixelPosition}px`,
              left: '0',
              right: '0',
            }}>
            <div className="bg-linear-to-r backdrop-blur-xs absolute -top-4 z-20 rounded-full border border-red-400 from-red-500 to-red-600 px-3 py-1 text-white shadow-lg">
              <span className="drop-shadow-xs text-xs font-medium">{currentTimePosition.time}</span>
            </div>

            <div className="relative h-0.5 w-full">
              <div className="bg-linear-to-r absolute inset-0 from-red-500 via-red-400 to-red-500 shadow-md"></div>
              <div className="blur-xs absolute inset-0 bg-red-400/40"></div>
              <div className="shadow-xs absolute -top-1 left-0 h-2 w-2 animate-pulse rounded-full border border-red-300 bg-red-500"></div>
            </div>
          </div>
        )}

        <div className="min-h-full">
          {HOURS.map((hour) => (
            <React.Fragment key={hour}>
              <HourGrouping hour={hour} isFirstInGroup={isFirstInTimeGroup(hour)} />

              <div className="grid h-20 grid-cols-8 border-b border-slate-100/60">
                {/* Time label */}
                <div className="bg-linear-to-r flex items-start justify-center border-r border-slate-200/60 from-slate-50/50 to-white/50 p-2">
                  <span className="shadow-xs rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-slate-600">
                    {hour === 0
                      ? '12 AM'
                      : hour === 12
                        ? '12 PM'
                        : hour > 12
                          ? `${hour - 12} PM`
                          : `${hour} AM`}
                  </span>
                </div>

                {/* Day slots with 15-minute intervals */}
                {DAYS.map((_, dayIndex) => {
                  const currentDate = weekDates[dayIndex]!
                  const timeZoneDate = new Date(
                    currentTime.toLocaleString('en-US', { timeZone: selectedTimeZone }),
                  )
                  const isToday = currentDate.toDateString() === timeZoneDate.toDateString()

                  return (
                    <div
                      key={`${dayIndex}-${hour}`}
                      className={`relative border-r border-slate-200/60 last:border-r-0 ${
                        isToday ? 'bg-[#6864ff]/8' : ''
                      }`}>
                      {MINUTES.map((minute) => {
                        const post = getPostForSlot(currentDate, hour, minute)
                        const isHalfHour = minute === 30
                        const isQuarterHour = minute === 15 || minute === 45

                        return (
                          <div key={`${dayIndex}-${hour}-${minute}`} className="relative">
                            <TimeSlot
                              date={currentDate.toISOString().split('T')[0]!}
                              hour={hour}
                              minute={minute}
                              className={`relative h-5 ${
                                isHalfHour
                                  ? 'border-b border-slate-200/60'
                                  : isQuarterHour
                                    ? 'border-b border-slate-100/60'
                                    : 'border-b border-slate-50/40'
                              } transition-colors last:border-b-0 hover:bg-slate-50/20`}>
                              {isHalfHour && (
                                <div className="absolute left-0 top-0 h-full w-1 bg-slate-300/40"></div>
                              )}
                              {isQuarterHour && (
                                <div className="absolute left-1 top-1/2 h-0.5 w-0.5 rounded-full bg-slate-400/60"></div>
                              )}
                            </TimeSlot>

                            {post && (
                              <div
                                className="pointer-events-none absolute left-0 right-0 z-20"
                                style={{
                                  top: `0px`,
                                  height: '76px',
                                }}>
                                <div className="pointer-events-auto relative h-full w-full">
                                  <PostItem post={post} />
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Drag Preview Time Indicator */}
      {dragPreview && (
        <div
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full transform"
          style={{
            left: `${dragPreview.x}px`,
            top: `${dragPreview.y - 10}px`,
          }}>
          <div
            className="backdrop-blur-xs rounded-xl border border-white/20 px-4 py-2 text-white shadow-2xl"
            style={{ backgroundColor: '#3F2AD8' }}>
            <div className="drop-shadow-xs text-sm font-medium">
              {formatDragTime(dragPreview.hour, dragPreview.minute, dragPreview.date).time}
            </div>
            <div className="drop-shadow-xs text-xs opacity-90">
              {formatDragTime(dragPreview.hour, dragPreview.minute, dragPreview.date).date}
            </div>
            <div
              className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 transform border-l-4 border-r-4 border-t-4 border-transparent"
              style={{ borderTopColor: '#3F2AD8' }}></div>
          </div>
        </div>
      )}
    </div>
  )
}
